import { Heading } from 'copilot-design-system';
import { Form, Formik } from 'formik';
import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import { EmailValidationRegex } from 'src/constants';
import { SignUpData } from 'src/features/billing/StripePaymentElement/StripePaymentElement';
import { StripePaymentMethod } from 'src/types/payment';

interface Props {
  onFormChanged: (data: SignUpData) => void;
  companiesDisabled: boolean;
  initialData: SignUpData;
  selectedSource: string | StripePaymentMethod | null;
}

export const hasValidateName = (name: string) => {
  if (!name || !name.trim()) return false;
  const formattedName = name.trim().split(' ');
  return formattedName.length >= 2;
};

export const hasValidateEmail = (email: string) => {
  return EmailValidationRegex.test(email);
};

const unAuthSchema = z.object({
  name: z
    .string({
      required_error: 'Name is required',
    })
    .refine((name) => hasValidateName(name), {
      message: 'Provide both first and last name',
    }),
  email: z
    .string({
      required_error: 'Email is required',
    })

    .refine((email) => hasValidateEmail(email), {
      message: 'Invalid email address',
    }),
  company: z.string().optional(),
});

export const UnAuthCheckoutForm: React.FC<Props> = ({
  onFormChanged,
  companiesDisabled,
  initialData,
  selectedSource,
}) => {
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={initialData}
      validationSchema={toFormikValidationSchema(unAuthSchema)}
      onSubmit={() => {}}
    >
      {({ values, errors, touched, handleBlur, handleChange }) => {
        const handleFormChange = (
          event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          const { name, value } = event.target;
          handleChange(event);
          onFormChanged({ ...values, [name]: value });
        };
        return (
          <Form>
            <div className="flex flex-col gap-2 mb-6">
              {selectedSource === 'card' && (
                <>
                  <div>
                    <Heading className="text-[14px] text-black-heading mb-1">
                      Full Name
                    </Heading>
                    <TextField
                      value={values.name}
                      name="name"
                      error={!!(errors.name && touched.name)}
                      onBlur={handleBlur}
                      onChange={handleFormChange}
                      helperText={
                        errors.name && touched.name ? errors.name : ''
                      }
                      variant="outlined"
                      fullWidth
                      required
                      inputProps={{
                        className: 'text-black-heading !h-10 text-[14px]',
                      }}
                      placeholder="First and last name"
                    />
                  </div>
                  <div>
                    <Heading className="text-[14px] text-black-heading mb-1">
                      Email
                    </Heading>
                    <TextField
                      value={values.email}
                      name="email"
                      variant="outlined"
                      error={!!(errors.email && touched.email)}
                      onBlur={handleBlur}
                      onChange={handleFormChange}
                      helperText={
                        errors.email && touched.email ? errors.email : ''
                      }
                      fullWidth
                      required
                      inputProps={{
                        className: 'text-black-heading !h-10 text-[14px]',
                      }}
                      placeholder="Email address"
                    />
                  </div>
                </>
              )}
              {!companiesDisabled && (
                <div>
                  <div className="flex items-center mb-1">
                    <Heading className="text-black-heading">
                      {t(($) => $.company, { count: 1 })}
                    </Heading>
                    <Heading className=" text-gray-small">
                      &nbsp;- Optional
                    </Heading>
                  </div>
                  <TextField
                    value={values.company}
                    name="company"
                    error={!!(errors.company && touched.company)}
                    onBlur={handleBlur}
                    onChange={handleFormChange}
                    helperText={
                      errors.company && touched.company ? errors.company : ''
                    }
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      className: 'text-black-heading !h-10 text-[14px]',
                    }}
                    placeholder={t(($) => $.company_name)}
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
